// Shared shell for the A2 / A3 / A4 analyst tabs.
//
// Renders the 3-section template:
//   Section 1: Criteria Checklist (lane catalog slice).
//   Section 2: Additional Certifications (lane methodology certs).
//   Section 3: Lane Pass/Fail + DQR.
//
// Behaviors:
//   - Sections 2 and 3 are read-only when the deal is Closed or the
//     caller does not own this lane (getLaneEditPermission; B5 owns none;
//     server re-enforces). Section 1's rows gate their actions with
//     checklistItemActions and lock only on a Closed deal.
//   - When the lane's DealAssignment status === NotApplicable, the
//     entire tab renders the N/A banner and skips the sections.
//   - Cross-tab unlock: issuing a lane Pass/Fail invalidates the
//     LaneAssessments query key — <VerificationOfAnalystWorkSection>
//     subscribes to that same query and re-renders without a page
//     refresh.

import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Card, Badge } from "@/design-system/components/ui";
import { TabPlaceholderCard } from "../common/TabPlaceholderCard";
import { CriteriaChecklist } from "../CriteriaChecklist";
import { LanePassFailSection } from "../UnderwritingTab/sections/LanePassFailSection";
import { AdditionalCertificationsSection } from "../AdditionalCertifications";
import { useAnalystTabState } from "./useAnalystTabState";
import { getLaneEditPermission } from "@/lib/dealFlowPermissions";
import { useInternalUserLabels, fallbackUserLabel } from "@/api/internalUsers";
import { useDateFormatter } from "@/utils/datetime";
import type { CriteriaLane } from "@/api/dealFlow/types";
import type { DealDetailDto, DealPipelineStage } from "@/types/deals";

interface AnalystTabContentProps {
  deal: DealDetailDto;
  lane: CriteriaLane;
}

const LANE_LABEL: Record<CriteriaLane, string> = {
  A1: "A1 Underwriting",
  A2: "Financial Analysis",
  A3: "Legal Analysis",
  A4: "Business Analysis",
};

function stageAtOrPastUnderwriting(stage: DealPipelineStage): boolean {
  return (
    stage === "Underwriting" ||
    stage === "Closing" ||
    stage === "Closed"
  );
}

export function AnalystTabContent({ deal, lane }: AnalystTabContentProps) {
  const auth = useAuth();
  const state = useAnalystTabState(deal.id, lane);
  const laneLabel = LANE_LABEL[lane];
  const { dateTime } = useDateFormatter();

  // Resolve acting-user ids (last updater + N/A marker) to display names,
  // reusing the shared batched resolver — never render a raw GUID.
  const userLabels = useInternalUserLabels(
    [state.lastUpdatedByUserId, state.notApplicableByUserId].filter(
      (id): id is string => !!id,
    ),
  );
  const nameFor = (id: string | null): string =>
    id ? (userLabels.lookup(id)?.displayName ?? fallbackUserLabel(id)) : "—";

  const permSet = useMemo(
    () => new Set(auth.user?.permissions ?? []),
    [auth.user?.permissions],
  );
  const roleCodes = auth.user?.roleCodes ?? [];

  const isClosed = deal.stage === "Closed";
  const stageGated = !stageAtOrPastUnderwriting(deal.stage);

  // Lane edit gate. Server-side enforcement is canonical; this only hides
  // UI affordances. B5 (QA & Audit) does not satisfy any ownership branch
  // and falls through to read-only — same as any other non-owner.
  const editPermission = useMemo(
    () => getLaneEditPermission(permSet, { lane, userRoleCodes: roleCodes }),
    [permSet, lane, roleCodes],
  );
  const readOnly = isClosed || !editPermission.canEdit;

  const [openSection, setOpenSection] = useState<number>(1);
  useEffect(() => {
    if (state.isLoading) return;
    // Auto-expand the first incomplete section.
    if (!state.section1Complete) setOpenSection(1);
    else if (!state.section2Complete) setOpenSection(2);
    else if (!state.section3Complete) setOpenSection(3);
    else setOpenSection(0);
  }, [
    state.isLoading,
    state.section1Complete,
    state.section2Complete,
    state.section3Complete,
  ]);

  if (stageGated) {
    return (
      <TabPlaceholderCard
        title="Available once the deal reaches the Underwriting stage"
        body={`${laneLabel} activates when the deal advances to Underwriting.\n\nCurrent stage: ${deal.stage}.`}
      />
    );
  }

  if (state.isLoading) {
    return (
      <Card>
        <p
          className="text-small text-gray-mid italic m-0"
          data-testid={`analyst-tab-${lane}-loading`}
        >
          Loading {laneLabel}…
        </p>
      </Card>
    );
  }

  if (state.isNotApplicable) {
    return (
      <Card>
        <div
          className="flex flex-col gap-space-2"
          data-testid={`analyst-tab-${lane}-na`}
        >
          <h3 className="font-display text-h4 text-onyx m-0">
            {laneLabel} — Marked Not Applicable
          </h3>
          <p className="text-small text-onyx m-0">
            This lane was marked Not Applicable for this deal
            {state.notApplicableByUserId
              ? ` by ${nameFor(state.notApplicableByUserId)}`
              : ""}
            {state.notApplicableAt
              ? ` on ${dateTime(state.notApplicableAt)}`
              : ""}
            . No checklist work, additional certifications, or lane
            assessment is required.
          </p>
          <p className="text-small text-onyx m-0">
            A1&apos;s Section 6 verification slot for this lane is
            auto-completed.
          </p>
          <a
            href={`/pipeline/${deal.id}?tab=underwriting`}
            className="text-small text-gold-shadow hover:underline mt-space-2"
          >
            View Underwriting tab Section 3 →
          </a>
        </div>
      </Card>
    );
  }

  const sections: Array<{
    num: number;
    title: string;
    unlocked: boolean;
    statusBadge: React.ReactNode;
    body: () => React.ReactNode;
  }> = [
    {
      num: 1,
      title: "Criteria Checklist",
      unlocked: true,
      statusBadge: state.section1Complete ? (
        <Badge status="finalized">Complete</Badge>
      ) : (
        <Badge status="archived">
          {state.totalItemCount - state.pendingItemCount}/
          {state.totalItemCount} terminal
        </Badge>
      ),
      body: () => (
        <CriteriaChecklist
          dealId={deal.id}
          lane={lane}
          // Each row works out what its caller may do from the server's own
          // checklist rule (owner approval, 2026-10-13), so only a Closed deal
          // locks it here.
          readOnly={isClosed}
        />
      ),
    },
    {
      num: 2,
      title: "Additional Certifications",
      unlocked: true,
      statusBadge: state.section2Complete ? (
        <Badge status="finalized">Complete</Badge>
      ) : (
        <Badge status="archived">
          {state.totalCertCount - state.pendingCertCount}/
          {state.totalCertCount} resolved
        </Badge>
      ),
      body: () => (
        <AdditionalCertificationsSection
          dealId={deal.id}
          lane={lane}
          readOnly={readOnly}
        />
      ),
    },
    {
      num: 3,
      title: "Lane Pass / Fail + DQR",
      unlocked: state.canIssueLaneAssessment || state.section3Complete,
      statusBadge: state.section3Complete ? (
        <Badge status="gold">Issued</Badge>
      ) : state.canIssueLaneAssessment ? (
        <Badge status="archived">Ready</Badge>
      ) : (
        <Badge status="archived">Locked</Badge>
      ),
      body: () => (
        <>
          {!state.section3Complete && (
            <div className="mb-space-3 rounded-md border border-gold/40 bg-gold/10 px-space-3 py-space-2">
              <p className="text-caption text-onyx m-0">
                When you issue your lane Pass/Fail, A1&apos;s Section 6
                verification slot for {lane} becomes available for A1 to
                verify your work. This is final.
              </p>
            </div>
          )}
          <LanePassFailSection
            dealId={deal.id}
            lane={lane}
            readOnly={readOnly}
            additionalGate={{
              met: state.section2Complete,
              pendingLabel: `${state.pendingCertCount} additional certification(s) pending.`,
            }}
          />
        </>
      ),
    },
  ];

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid={`analyst-tab-${lane}`}
    >
      {isClosed && (
        <Card>
          <p className="text-small text-onyx m-0">
            This deal is <strong>Closed</strong>. The {laneLabel} tab is
            read-only.
          </p>
        </Card>
      )}
      {!isClosed && !editPermission.canEdit && (
        <Card>
          <p
            className="text-small text-gray-mid italic m-0"
            data-testid={`analyst-tab-${lane}-readonly-banner`}
          >
            {editPermission.reason} You can review every section but
            write actions are disabled.
          </p>
        </Card>
      )}

      {sections.map((s) => {
        const expanded = openSection === s.num && s.unlocked;
        const lockedTitle =
          !s.unlocked && s.num === 3
            ? `Locked — ${state.blockingReason}`
            : undefined;
        return (
          <section
            key={s.num}
            className="border border-border-light rounded-md bg-white-pure"
            data-testid={`analyst-tab-${lane}-section-${s.num}`}
          >
            <button
              type="button"
              className={`w-full flex items-center justify-between gap-space-3 px-space-4 py-space-3 text-left ${
                s.unlocked
                  ? "cursor-pointer hover:bg-white-warm"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                s.unlocked && setOpenSection(expanded ? 0 : s.num)
              }
              aria-expanded={expanded}
              aria-controls={`analyst-tab-${lane}-body-${s.num}`}
              disabled={!s.unlocked}
              title={lockedTitle}
            >
              <div className="flex items-center gap-space-3 min-w-0">
                {s.unlocked ? (
                  expanded ? (
                    <ChevronDown size={16} aria-hidden />
                  ) : (
                    <ChevronRight size={16} aria-hidden />
                  )
                ) : (
                  <Lock size={14} className="text-gray-mid" aria-hidden />
                )}
                <span className="font-display text-h4 text-onyx">
                  Section {s.num} · {s.title}
                </span>
              </div>
              {s.statusBadge}
            </button>
            {expanded && (
              <div
                id={`analyst-tab-${lane}-body-${s.num}`}
                className="border-t border-border-light p-space-3"
              >
                {s.body()}
              </div>
            )}
          </section>
        );
      })}

      <Card>
        <p
          className="text-caption text-gray-mid m-0"
          data-testid={`analyst-tab-${lane}-footer`}
        >
          {state.lastUpdatedAt
            ? `Last updated by ${nameFor(state.lastUpdatedByUserId)} at ${dateTime(state.lastUpdatedAt)}.`
            : "No activity recorded on this lane yet."}
        </p>
      </Card>
    </div>
  );
}
